import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChildren} from '@angular/core';
import {VotingSectionComponent} from '@modules/voting/voting-section/voting-section.component';
import {VotingControlService} from '@modules/voting/voting-control.service';
import {ActivatedRoute, Params} from '@angular/router';
import {LocalVotingStatus, VotingDisplayMode} from '@modules/voting/models/enums';
import {CaptchaService} from '@core/services/captcha/captcha.service';
import {StateService} from '@core/services/state.service';
import {Subscription} from 'rxjs';
import {SEOService} from '@core/services/seo.service';
import {TaboolaCategories} from '@shared/models/taboola/taboola-categories';

@Component({
  selector: 'app-voting-controller',
  templateUrl: './voting-controller.component.html',
  styleUrls: ['./voting-controller.component.scss'],
  providers: [
    { provide: VotingControlService },
    { provide: CaptchaService }
  ]
})
export class VotingControllerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() votingId = undefined;
  @Input() setMetadata = false;
  @ViewChildren(VotingSectionComponent) votingSections;

  mode: VotingDisplayMode;
  revoteAllowanceElapsedSubscription: Subscription;

  constructor(
    private activeRoute: ActivatedRoute,
    private seoService: SEOService,
    private changeDetector: ChangeDetectorRef,
    public votingControl: VotingControlService,
    public captchaService: CaptchaService
    ) {
  }

  async ngOnInit() {
    if (this.votingId === undefined) {
      this.activeRoute.params.subscribe((params: Params) => {
        this.votingId = this.votingControl.votingId = params.votingId;
        this.initVoting();
      });
    }

    this.revoteAllowanceElapsedSubscription = this.votingControl.onRevoteAllowanceElapsed.subscribe(() => this.initVoting());
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.votingId?.previousValue === undefined && changes.votingId?.currentValue) {
      this.checkInitFromInput();
    }
  }

  ngOnDestroy(): void {
    this.revoteAllowanceElapsedSubscription?.unsubscribe();
  }

  public async checkInitFromInput(): Promise<void> {
    // if id is injected via input it might be null first, check in init and changes
    if (this.votingId != null) {
      this.votingControl.votingId = this.votingId;
      this.initVoting();
    }
  }

  private async initVoting(): Promise<void> {
    this.mode = this.activeRoute.snapshot.data['mode'] || VotingDisplayMode.SUBMISSION;
    const voting = await this.votingControl.fetchVoting();

    if (this.setMetadata) {
      this.setTaboolaMetadata();
    }

    if (!voting.isClosed && StateService.isBrowser) {
      await this.votingControl.openVotingSession();
    }

    if (this.mode === VotingDisplayMode.RESULTS) {
      // if user is routed directly to results, e.g. through link
      if (!this.votingControl.checkSufficientVotes()) {
        this.votingControl.displayMode = VotingDisplayMode.INSUFFICIENT_VOTES;
        return;
      }

      this.mode = this.votingControl.displayMode = VotingDisplayMode.RESULTS;

      if (voting.isClosed) {
        this.votingControl.localVotingStatus = LocalVotingStatus.CLOSED;
      } else {
        this.votingControl.localVotingStatus = this.votingControl.userHasAlreadyVoted() ? LocalVotingStatus.REVIEW : LocalVotingStatus.RESULTS;
      }
    } else if (this.mode === VotingDisplayMode.SUBMISSION && this.votingControl.userHasAlreadyVoted()) {
      this.votingControl.showResults(LocalVotingStatus.REVIEW);
    } else {
      if (voting.isClosed) {
        this.votingControl.showResults(LocalVotingStatus.CLOSED);
        return;
      }

      this.votingControl.displayMode = VotingDisplayMode.SUBMISSION;
      this.votingControl.localVotingStatus = LocalVotingStatus.OPEN_ELIGIBLE;
      this.changeDetector.detectChanges();
    }
  }

  private setTaboolaMetadata(): void {
    const timestamp = this.votingControl.voting?.settings?.timestamps?.end;
    this.seoService.setTaboolaCategory(TaboolaCategories.Voting);
    if (timestamp && timestamp > 0) {
      this.seoService.setTaboolaValidUntil(timestamp);
    }
  }

  get isVotingSelectionValid(): boolean {
    if (this.votingSections === undefined) {
      return false;
    }
    const sections: VotingSectionComponent[] = this.votingSections.toArray();

    if (!sections.length) {
      return;
    }

    let globalSelectionSum = 0;
    sections.forEach((section: VotingSectionComponent) => globalSelectionSum += section.nrSelectedItems);

    if (this.votingControl.voting.settings.selection_limits.min <= globalSelectionSum
      && globalSelectionSum <= this.votingControl.voting.settings.selection_limits.max) {
      this.captchaService.loadCaptcha();
      return true;
    }

    for (const section of sections) {
      if (!section?.selectionValid) {
        return false;
      }
    }

    this.captchaService.loadCaptcha();
    return true;
  }

  public get emptySections(): VotingSectionComponent[] {
    const emptySections = [];
    this.votingSections.forEach((section: VotingSectionComponent) => {
      if (section.nrSelectedItems === 0) {
        emptySections.push(section.title);
      }
    });

    return emptySections;
  }
}
